import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Ticket, LogOut, Wifi, Menu, X, ChevronLeft } from "lucide-react";
import NotificationBell from "./NotificationBell";
import { useCMS } from "../context/CMSContext";

export default function TechLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { settings } = useCMS();
  const companyName = settings?.company_name || "ISP Manager";

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  const menuItems = [
    {
      path: "/tech/tickets",
      icon: Ticket,
      label: "Support Tickets",
      testId: "tech-nav-tickets",
    },
    {
      path: "/tech/new-applications",
      icon: Ticket,
      label: "New Application",
      testId: "tech-nav-new-applications",
    },
  ];

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-800 px-4 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
            <Wifi className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-white">{companyName} - Tech</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-white">
            <NotificationBell />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-slate-700"
            data-testid="tech-mobile-menu-toggle"
          >
            {sidebarOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
        fixed lg:sticky top-0 left-0 z-50 h-screen bg-slate-800 text-white flex flex-col
        transition-all duration-300 ease-in-out
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        ${sidebarCollapsed ? "lg:w-20" : "lg:w-64"}
        w-64
      `}
      >
        <div
          className={`p-4 lg:p-6 border-b border-slate-700 ${sidebarCollapsed ? "lg:px-3" : ""}`}
        >
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Wifi className="w-5 h-5 text-white" />
            </div>
            <div className={`${sidebarCollapsed ? "lg:hidden" : ""}`}>
              <h1 className="text-lg font-bold">{companyName}</h1>
              <p className="text-xs text-slate-400">Tech Portal</p>
            </div>
          </div>
        </div>

        {/* Collapse Toggle (Desktop only) */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-slate-700 border border-slate-600 rounded-full items-center justify-center hover:bg-slate-600 transition-colors"
          data-testid="tech-sidebar-collapse-toggle"
        >
          <ChevronLeft
            className={`w-4 h-4 text-slate-300 transition-transform ${sidebarCollapsed ? "rotate-180" : ""}`}
          />
        </button>

        <nav className={`flex-1 p-4 ${sidebarCollapsed ? "lg:p-2" : ""}`}>
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={item.testId}
              onClick={() => setSidebarOpen(false)}
              title={sidebarCollapsed ? item.label : undefined}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                sidebarCollapsed ? "lg:px-3 lg:justify-center" : ""
              } ${
                isActive(item.path)
                  ? "bg-indigo-700 text-white"
                  : "text-slate-300 hover:bg-slate-700"
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span
                className={`font-medium ${sidebarCollapsed ? "lg:hidden" : ""}`}
              >
                {item.label}
              </span>
            </Link>
          ))}
        </nav>

        <div
          className={`p-4 border-t border-slate-700 ${sidebarCollapsed ? "lg:p-2" : ""}`}
        >
          <div className={`mb-3 ${sidebarCollapsed ? "lg:hidden" : ""}`}>
            <p className="text-sm font-semibold truncate">{user.full_name}</p>
            <p className="text-xs text-slate-400 truncate">{user.email}</p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="tech-logout-btn"
            variant="outline"
            title={sidebarCollapsed ? "Logout" : undefined}
            className={`w-full gap-2 text-slate-300 border-slate-600 hover:bg-slate-700 ${
              sidebarCollapsed ? "lg:justify-center lg:px-2" : "justify-start"
            }`}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            <span className={sidebarCollapsed ? "lg:hidden" : ""}>Logout</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pt-14 lg:pt-0">
        {/* Desktop Notification Bell - Top Right */}
        <div className="hidden lg:flex justify-end px-6 py-3 border-b border-slate-200 bg-white">
          <NotificationBell />
        </div>
        {children}
      </main>
    </div>
  );
}
